import {
  useDownshiftEnvironment,
  useLayoutPropsUtil,
} from "../../internal/hooks";
import Popover from "../Popover";
import {
  ComboboxSelectProps,
  SelectMultipleProps,
  SelectSingleProps,
} from "./ComboboxTypes";
import { ComboboxContext } from "./internal/ComboboxContext";
import {
  defaultItemToKey,
  defaultItemToString,
  getScrollParent,
  isMultiple,
  SELECT_ALL,
} from "./internal/ComboboxUtils";
import cx from "classnames";
import styles from "./Combobox.module.scss";
import { useEffect, useRef, useState } from "react";
import { useGroups } from "./internal/useGroups";
import {
  useMultipleSelection as useDownshiftMultipleSelection,
  useSelect as useDownshiftSelect,
  UseSelectStateChangeTypes,
} from "downshift";

const DEBUG_STATE_REDUCERS = false;

/**
 * NOTE: There is a lot of duplicated code here! Here's a list of the /actual/
 *       differences between select-only and regular combobox to help with
 *       refactoring this later!
 *
 *       1) Using Downshift's `useSelect` hook instead of `useCombobox`
 *          - Swapping out the stateChangeTypes for their equivalents
 *       2) Since there's no input, we don't need to passthrough `getInputProps`
 *       3) Since we don't do any sorting or filtering, we can remove:
 *          - onInputValueChange function and prop
 *            - setInputValue function
 *          - onIsOpenChange function
 *          - filterOptions prop
 *          - internal `items` state and useEffect to keep it in sync
 *            - useEffect to track first render
 *       4) Multiple-specific
 *          - Remove `disableClearInputValueOnSelectItem` prop
 *          - No need to store `setInputValue` function
 *          - Remove all state reducer references to `inputValue`
 *          - Remove `onStateChange` function from `useMultipleSelection`
 */

export const SelectSingle = function <Item>(props: SelectSingleProps<Item>) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    disabled,
    readOnly,
    items,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    disableClearSelection = false,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    loading = false,
    minPopoverHeight,
    root,
    environment: environmentProp,
    groupBy,
    groupToString,
    groupSorter,
    ...rest
  } = componentProps;

  const ComboboxClassNames = cx(styles["combobox"], className);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  const [forceCloseOnSelectItems, setForceCloseOnSelectItems] = useState<
    Item[]
  >([]);
  const [, setForceClearInputValueOnSelectItems] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | HTMLUListElement | null>(null);
  const handleOutsidePress = (_e: MouseEvent) => {
    if (disableCloseOnClickOutside) return;
    closeMenu();
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const { groups, orderedItems } = useGroups({
    items,
    groupBy,
    groupToString,
    groupSorter,
  });

  const {
    isOpen,
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    inputValue,
    closeMenu,
  } = useDownshiftSelect<Item>({
    ...rest,
    items: orderedItems,
    itemToString,
    itemToKey,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        case useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useDownshiftSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen:
              open != null
                ? open
                : forceCloseOnSelectItems.includes(changes.selectedItem as Item)
                  ? false
                  : disableCloseOnSelectItem
                    ? true
                    : changes.isOpen,
            highlightedIndex: disableCloseOnSelectItem
              ? state.highlightedIndex
              : changes.highlightedIndex,
          };
        default:
          return changes;
      }
    },
    onSelectedItemChange(changes) {
      const { selectedItem } = changes;

      if (onChange != null)
        onChange(selectedItem != null ? selectedItem : null);
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
  });

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  return (
    <ComboboxContext.Provider
      value={{
        multiple: false,
        select: true,
        disableClearSelection,
        items: orderedItems,
        itemToString,
        itemToKey,
        isOpen,
        getLabelProps,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        selectedItem,
        selectedItems: [],
        disabledItems,
        setDisabledItems,
        setForceCloseOnSelectItems,
        setForceClearInputValueOnSelectItems,
        selectItem,
        inputValue,
        isDisabled,
        isReadOnly,
        setIsReadOnly,
        setIsDisabled,
        hasDisabledPopover,
        setHasDisabledPopover,
        hasEmpty,
        setHasEmpty,
        loading,
        referenceRef,
        contentRef,
        closeMenu,
        groups,
        groupBy,
        groupToString,
        listRef,
      }}
    >
      <div
        className={ComboboxClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          open={isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onClickOutside={handleOutsidePress}
          minHeight={minPopoverHeight}
        >
          {children}
        </Popover>
      </div>
    </ComboboxContext.Provider>
  );
};

const SelectMultiple = function <Item>(props: SelectMultipleProps<Item>) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    items,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    onSelectedItemsChange,
    onStateChange,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    loading = false,
    minPopoverHeight,
    root,
    disabled,
    readOnly,
    stateReducer: _,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp,
    groupBy,
    groupToString,
    groupSorter,
    selectAll,
    ...rest
  } = componentProps;

  const ComboboxClassNames = cx(styles["combobox"], className);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  const [forceCloseOnSelectItems, setForceCloseOnSelectItems] = useState<
    Item[]
  >([]);
  const [, setForceClearInputValueOnSelectItems] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | HTMLUListElement | null>(null);

  const handleOutsidePress = (_e: MouseEvent) => {
    if (disableCloseOnClickOutside) return;
    closeMenu();
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const { groups, orderedItems: orderedItemsBase } = useGroups({
    items,
    groupBy,
    groupToString,
    groupSorter,
  });

  const orderedItems = selectAll
    ? [SELECT_ALL as Item, ...orderedItemsBase]
    : orderedItemsBase;

  const {
    getSelectedItemProps,
    getDropdownProps,
    reset,
    selectedItems,
    addSelectedItem,
    removeSelectedItem,
    setSelectedItems,
  } = useDownshiftMultipleSelection<Item>({
    itemToKey,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp ?? environment,
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        // the Chip itself will handle "Backspace" and "Delete"
        // just return previous state
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownBackspace:
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownDelete:
          return state;

        case useDownshiftMultipleSelection.stateChangeTypes
          .FunctionRemoveSelectedItem:
          return { ...changes, activeIndex: -1 };

        case useDownshiftMultipleSelection.stateChangeTypes.FunctionReset:
          return { ...changes, selectedItems: [] };

        default:
          return changes;
      }
    },
    onStateChange,
    onSelectedItemsChange(changes) {
      const { selectedItems } = changes;
      if (onChange != null) onChange(selectedItems);
      if (onSelectedItemsChange != null) onSelectedItemsChange(changes);
    },
  });

  const {
    isOpen,
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    inputValue,
    closeMenu,
  } = useDownshiftSelect<Item>({
    ...rest,
    items: orderedItems,
    itemToString,
    itemToKey,
    selectedItem: null,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        case useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useDownshiftSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen:
              open != null
                ? open
                : forceCloseOnSelectItems.includes(changes.selectedItem as Item)
                  ? false
                  : disableCloseOnSelectItem
                    ? true
                    : changes.isOpen,
            highlightedIndex: disableCloseOnSelectItem
              ? state.highlightedIndex
              : changes.highlightedIndex,
          };

        default:
          return changes;
      }
    },
    onStateChange(changes) {
      const { type, selectedItem: newSelectedItem } = changes;

      switch (type) {
        case useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useDownshiftSelect.stateChangeTypes.ItemClick:
        case useDownshiftSelect.stateChangeTypes.ToggleButtonBlur:
          if (changes.selectedItem === SELECT_ALL) {
            selectAll?.onSelection();
            return {
              ...changes,
              selectedItem: null,
            };
          }
          if (newSelectedItem != null) {
            const itemAlreadySelected = selectedItems.some(
              (item) => itemToKey(item) === itemToKey(newSelectedItem),
            );

            if (itemAlreadySelected) {
              removeSelectedItem(newSelectedItem);
            } else {
              addSelectedItem(newSelectedItem);
            }
          }
          break;

        default:
          break;
      }

      onStateChange?.(changes);
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
    onHighlightedIndexChange(changes) {
      if (
        selectAll &&
        changes.highlightedIndex === 0 &&
        (
          [
            useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownArrowDown,
            useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp,
            useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownPageDown,
            useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownPageUp,
            useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownHome,
            useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownEnd,
          ] as UseSelectStateChangeTypes[]
        ).includes(changes.type) &&
        listRef.current
      ) {
        // Scroll to the top of the list when select all is highlighted
        const scrollParent = getScrollParent(listRef.current);
        if (scrollParent) {
          scrollParent.scrollTop = 0;
        }
      }
    },
  });

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  return (
    <ComboboxContext.Provider
      value={{
        multiple: true,
        select: true,
        items: orderedItems,
        itemToString,
        itemToKey,
        isOpen,
        getLabelProps,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        selectedItem,
        selectedItems,
        disabledItems,
        setDisabledItems,
        setForceCloseOnSelectItems,
        setForceClearInputValueOnSelectItems,
        getSelectedItemProps,
        getDropdownProps,
        reset,
        addSelectedItem,
        removeSelectedItem,
        setSelectedItems,
        selectItem,
        inputValue,
        isDisabled,
        isReadOnly,
        setIsReadOnly,
        setIsDisabled,
        hasDisabledPopover,
        setHasDisabledPopover,
        hasEmpty,
        setHasEmpty,
        loading,
        referenceRef,
        contentRef,
        closeMenu,
        groupBy,
        groupToString,
        groups,
        listRef,
        selectAll,
      }}
    >
      <div
        className={ComboboxClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          open={!isDisabled && isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onClickOutside={handleOutsidePress}
          minHeight={minPopoverHeight}
        >
          {children}
        </Popover>
      </div>
    </ComboboxContext.Provider>
  );
};

/**
 * ComboboxSelect component for selecting items from a predefined list.
 *
 * Features:
 * - Single and multiple item selection from predefined options
 * - Dropdown-style selection interface
 * - Grouping support with custom group labels and sorting
 * - Loading states with spinner indicators
 * - Disabled and read-only states
 * - Customizable popover behavior and positioning
 * - Full accessibility support with ARIA attributes
 * - Keyboard navigation support
 * - Layout utility props for positioning and spacing
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Combobox.Select
 *   items={items}
 *   itemToString={(item) => item.name}
 *   onChange={setSelectedItem}
 * >
 *   <Combobox.SelectTrigger label="Select an item" />
 *   <Combobox.Content>
 *     {({ items }) => (
 *       <Combobox.List>
 *         {items.map((item, i) => (
 *           <Combobox.Item key={item.id} item={item} index={i}>
 *             {item.name}
 *           </Combobox.Item>
 *         ))}
 *       </Combobox.List>
 *     )}
 *   </Combobox.Content>
 * </Combobox.Select>
 */

export const ComboboxSelect = function <Item>(
  props: ComboboxSelectProps<Item>,
) {
  if (isMultiple(props)) {
    return <SelectMultiple<Item> {...props} />;
  } else {
    return <SelectSingle<Item> {...props} />;
  }
};
